import React, { useMemo } from "react";
import { suggestPackageCombos } from "../data/packages";
import {
  Sparkles,
  ShoppingBag,
  ExternalLink,
  Lock,
  Star,
  BadgePercent,
  Layers,
} from "lucide-react";

const formatNum = (n) => new Intl.NumberFormat("en-US").format(n);
const formatSar = (n) =>
  Number.isInteger(n) ? formatNum(n) : n.toFixed(2).replace(/\.?0+$/, "");

// عناوين وأيقونات الشارات — التصنيف الرئيسي هو الأول في `badges`.
const BADGE_META = {
  cheapest: { label: "الأرخص", icon: BadgePercent },
  least_items: { label: "الأقل عناصر", icon: Layers },
};

// -----------------------------------------------------------------------
// بطاقة منتج داخل الاقتراح — تتبع نفس أسلوب قسم "باقات CP الرسمية".
// -----------------------------------------------------------------------
const ProductCard = ({ item }) => {
  const { pkg, qty } = item;
  const available = !!pkg.link;

  return (
    <div
      data-testid={`suggestion-product-${pkg.cp}`}
      className="group relative rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-[#C9A96A]/40 transition-all overflow-hidden flex flex-col"
    >
      {/* الصورة */}
      <div className="relative aspect-square bg-[#0A1128] flex items-center justify-center p-3 sm:p-4">
        <img
          src={pkg.image}
          alt={pkg.name}
          loading="lazy"
          className="w-full h-full object-contain drop-shadow-[0_10px_20px_rgba(201,169,106,0.15)] group-hover:scale-105 transition-transform duration-500"
        />
        {qty > 1 && (
          <span className="absolute top-2 end-2 text-[10px] font-black px-1.5 py-0.5 rounded-md bg-[#C9A96A]/95 text-[#0A1128] shadow">
            ×{qty}
          </span>
        )}
      </div>

      {/* المحتوى */}
      <div className="p-3 sm:p-3.5 flex-1 flex flex-col gap-2">
        {/* كمية CP */}
        <div className="text-center">
          <div className="text-white font-black text-lg sm:text-xl tabular-nums leading-none">
            {formatNum(pkg.cp)}{" "}
            <span className="text-[#E5CB94] text-xs font-bold">CP</span>
          </div>
        </div>

        {/* السعر */}
        <div className="text-center">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-white font-black text-base sm:text-lg tabular-nums">
              {formatSar(pkg.price)}
            </span>
            <span className="text-[#C9A96A] font-bold text-[11px]">ريال</span>
          </div>
          {qty > 1 && (
            <div className="text-[10px] text-white/50 mt-0.5 tabular-nums" dir="ltr">
              {formatSar(pkg.price)} × {qty} = {formatSar(item.subtotal)} SAR
            </div>
          )}
        </div>

        {/* الكمية المطلوبة (نصّيًا داخل البطاقة) */}
        {qty > 1 && (
          <div className="text-center text-[10px] font-bold text-[#E5CB94] bg-[#C9A96A]/10 border border-[#C9A96A]/25 rounded-md py-1">
            الكمية المطلوبة: ×{qty}
          </div>
        )}

        {/* زر الشراء */}
        {available ? (
          <a
            href={pkg.link}
            target="_blank"
            rel="noreferrer"
            data-testid={`suggestion-buy-${pkg.cp}`}
            className="mt-auto w-full inline-flex items-center justify-center gap-1 h-9 px-2.5 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] font-black text-[11px] hover:brightness-110 transition"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            شراء الآن
            <ExternalLink className="w-3 h-3 opacity-70" />
          </a>
        ) : (
          <button
            type="button"
            disabled
            data-testid={`suggestion-locked-${pkg.cp}`}
            className="mt-auto w-full inline-flex items-center justify-center gap-1 h-9 px-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-white/40 font-bold text-[11px] cursor-not-allowed"
          >
            <Lock className="w-3.5 h-3.5" />
            غير متوفر حاليًا
          </button>
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------
// بطاقة اقتراح كاملة: عنوان + شبكة منتجات + ملخّص
// -----------------------------------------------------------------------
const SuggestionCard = ({ suggestion, isRecommended, neededCP }) => {
  const { badges, items, totalCP, totalPrice, excess, totalItems } = suggestion;

  // العنوان الرئيسي هو تسمية أول شارة.
  const primary = badges[0];
  const primaryLabel = BADGE_META[primary].label;
  const PrimaryIcon = BADGE_META[primary].icon;

  // شارات ثانوية (باقي الشارات) — تُعرض بشكل هادئ بجانب العنوان.
  const secondary = badges.slice(1);

  // منطق أعمدة شبكة المنتجات: نضمن حجمًا مقبولًا على 390px.
  const cols =
    items.length === 1
      ? "grid-cols-1 sm:grid-cols-2"
      : items.length === 2
      ? "grid-cols-2"
      : "grid-cols-2 sm:grid-cols-3";

  return (
    <div
      data-testid={`suggestion-${primary}`}
      className={`rounded-2xl border p-4 sm:p-5 flex flex-col gap-4 bg-gradient-to-br from-white/[0.04] to-white/[0.01] ${
        isRecommended
          ? "border-[#C9A96A]/60 ring-2 ring-[#C9A96A]/25 shadow-lg shadow-[#B08A4A]/10"
          : "border-white/10"
      }`}
    >
      {/* العنوان */}
      <div className="flex items-center gap-2 flex-wrap">
        {isRecommended && (
          <span className="inline-flex items-center gap-1 text-[11px] font-black px-2.5 py-1 rounded-full bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] shadow">
            <Star className="w-3 h-3" />
            موصى به
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <PrimaryIcon className="w-4 h-4 text-[#E5CB94]" />
          <span className="text-white font-black text-sm sm:text-base">
            {primaryLabel}
          </span>
        </div>
        {secondary.map((b) => {
          const Icon = BADGE_META[b].icon;
          return (
            <span
              key={b}
              className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-white/70"
            >
              <Icon className="w-3 h-3" />
              {BADGE_META[b].label}
            </span>
          );
        })}
      </div>

      {/* شبكة المنتجات */}
      <div className={`grid gap-2.5 ${cols}`}>
        {items.map((it) => (
          <ProductCard key={it.pkg.id} item={it} />
        ))}
      </div>

      {/* الملخّص */}
      <div className="rounded-xl bg-white/[0.02] border border-white/10 p-3 sm:p-4 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-2">
          <MiniStat label="CP الإجمالي" value={`${formatNum(totalCP)} CP`} />
          <MiniStat label="CP المطلوب" value={`${formatNum(neededCP)} CP`} />
          <MiniStat
            label="CP الزائد"
            value={excess === 0 ? "0 CP" : `+${formatNum(excess)} CP`}
            tone={excess === 0 ? "emerald" : "white"}
          />
          <MiniStat label="عدد الباقات" value={`${totalItems}`} />
        </div>

        <div className="flex items-baseline justify-between gap-3 rounded-xl bg-gradient-to-l from-[#C9A96A]/12 to-transparent border border-[#C9A96A]/25 px-3 py-3">
          <span className="text-white/70 text-xs font-bold">
            إجمالي السعر
          </span>
          <div className="flex items-baseline gap-1">
            <span className="text-white font-black text-2xl sm:text-3xl tabular-nums">
              {formatSar(totalPrice)}
            </span>
            <span className="text-[#E5CB94] font-bold text-sm">ريال</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-[11px] text-white/60">
          <span>طريقة الدفع</span>
          <span className="font-bold text-white/80">تحويل بنكي</span>
        </div>
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, tone = "white" }) => (
  <div className="rounded-lg bg-white/[0.02] border border-white/5 px-2.5 py-1.5">
    <div className="text-[10px] text-white/50">{label}</div>
    <div
      className={`font-black tabular-nums text-sm ${
        tone === "emerald" ? "text-emerald-300" : "text-white"
      }`}
    >
      {value}
    </div>
  </div>
);

// -----------------------------------------------------------------------
// اللوحة الرئيسية
// -----------------------------------------------------------------------
const SuggestionsPanel = ({ neededCP, title, compact }) => {
  const suggestions = useMemo(
    () => suggestPackageCombos(neededCP),
    [neededCP]
  );

  if (!neededCP || neededCP <= 0 || suggestions.length === 0) return null;

  return (
    <div
      data-testid="smart-suggestions-panel"
      className="rounded-2xl bg-gradient-to-br from-[#C9A96A]/[0.05] to-white/[0.01] border border-[#C9A96A]/25 p-4 sm:p-5"
    >
      <div className="flex items-start gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-white font-black text-sm sm:text-base">
            {title || "اقتراحات ذكية لشراء CP"}
          </div>
          <div className="text-white/50 text-[11px] mt-0.5">
            أفضل تركيبات لتغطية{" "}
            <span className="text-[#E5CB94] font-bold">
              {formatNum(neededCP)} CP
            </span>{" "}
            من الباقات الرسمية
          </div>
        </div>
      </div>

      <div
        className={`grid gap-3 ${
          compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"
        }`}
      >
        {suggestions.map((s, i) => (
          <SuggestionCard
            key={s.badges.join("-")}
            suggestion={s}
            neededCP={neededCP}
            isRecommended={i === 0 && s.badges.includes("cheapest")}
          />
        ))}
      </div>
    </div>
  );
};

export default SuggestionsPanel;
